/**
 * Landing page photography.
 *
 * Paths point at `public/images/` so the files are swappable without a rebuild
 * of this module, and every entry carries its own alt text — an image slot and
 * its description belong together.
 *
 * Licensing and replacement notes: `apps/web/IMAGES.md`.
 */
export interface SiteImage {
  src: string;
  alt: string;
  /** Intrinsic size, so the browser reserves space and the page does not jump. */
  width: number;
  height: number;
}

const PET_CARE: SiteImage = {
  src: '/images/category-pet-care.jpg',
  alt: 'A teenager walking a dog along a country path',
  width: 700,
  height: 1050,
};

const TUTORING: SiteImage = {
  src: '/images/category-tutoring.jpg',
  alt: 'Three students working through a textbook together at a desk',
  width: 700,
  height: 467,
};

const YARD: SiteImage = {
  src: '/images/category-yard-outdoor.jpg',
  alt: 'Two young people sweeping and clearing a front yard',
  width: 700,
  height: 467,
};

const COMMUNITY: SiteImage = {
  src: '/images/category-community-help.jpg',
  alt: 'A smiling young volunteer in a volunteer t-shirt giving a thumbs up outdoors',
  width: 700,
  height: 467,
};

const ONLINE: SiteImage = {
  src: '/images/category-online-digital.jpg',
  alt: 'A teenager sketching on a tablet at a sunlit desk beside a computer',
  width: 700,
  height: 467,
};

const RETAIL: SiteImage = {
  src: '/images/category-retail.jpg',
  alt: 'A young shop worker in an apron smiling behind a counter',
  width: 700,
  height: 467,
};

const BABYSITTING: SiteImage = {
  src: '/images/category-babysitting.jpg',
  alt: 'A young babysitter playing at a table with two small children',
  width: 700,
  height: 467,
};

export const HERO_IMAGE: SiteImage = PET_CARE;

export const EARNINGS_IMAGE: SiteImage = {
  src: '/images/hero-teens-working.jpg',
  alt: 'Three teenagers working together on a laptop and books in a bright library',
  width: 1400,
  height: 933,
};

export const PARENTS_IMAGE: SiteImage = {
  src: '/images/parents-teen-laptop.jpg',
  alt: 'A parent and their teenage children looking at a laptop together at home',
  width: 1000,
  height: 1500,
};

export const CLOSING_IMAGE: SiteImage = COMMUNITY;

/**
 * The "Choose Your Path" collage. Two card photos sit on top of three loose
 * shots that stagger up and down behind them.
 */
export const PATH_IMAGES: Record<'teen' | 'teenPlus', SiteImage> = {
  teen: TUTORING,
  teenPlus: COMMUNITY,
};

export const PATH_COLLAGE: SiteImage[] = [PET_CARE, YARD, ONLINE];

/** Keyed by the step number in `HOW_IT_WORKS`. */
export const STEP_IMAGES: SiteImage[] = [
  ONLINE,
  PARENTS_IMAGE,
  RETAIL,
  COMMUNITY,
  PET_CARE,
  BABYSITTING,
];
